import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { api } from '../services/api';
import { ContactRequest, Profile } from '../types';
import { ContactInfoModal } from '../components/ContactInfoModal';
import { RatingModal } from '../components/RatingModal';
import { supabase } from '../services/supabase';
import { formatAnnee } from '../utils/formatAnnee';

interface ContactRequestsPageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const ContactRequestsPage: React.FC<ContactRequestsPageProps> = ({ showToast }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshNotifications } = useNotifications();
  const [requests, setRequests] = useState<ContactRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<ContactRequest[]>([]);
  const [activeTab, setActiveTab] = useState<'received' | 'sent'>('received');
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [showContactInfoModal, setShowContactInfoModal] = useState(false);
  const [selectedStudentUserId, setSelectedStudentUserId] = useState<string | null>(null);
  const [ratingOpen, setRatingOpen] = useState(false);
  const [ratingTargetProfileId, setRatingTargetProfileId] = useState<string | null>(null);

  const loadRequests = React.useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Récupérer le profil de l'utilisateur
      const { data: profile, error: profileError } = await api.profiles.getByUserId(user.id);
      
      if (profileError) {
        showToast('Erreur lors du chargement du profil', 'error');
        return;
      }
      
      // Charger les demandes reçues (si tuteur) ET envoyées (si étudiant)
      await Promise.all([
        loadReceivedRequests(profile),
        loadSentRequests(profile)
      ]);
      
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setLoading(false);
    }
  }, [user, showToast]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReceivedRequests = async (profile: any) => {
    if (!profile) {
      setRequests([]);
      return;
    }

      // Récupérer tous les matchs pour ce tuteur (SAUF les refusés)
      const { data: allMatches, error: matchesError } = await supabase
        .from('matchs')
        .select('*')
        .eq('repetiteur_id', profile.id)
        .eq('like_etudiant', true)  // L'étudiant a fait une demande
        .neq('liked', false)  // Exclure les demandes refusées
        .order('created_at', { ascending: false });

      if (matchesError) {
        showToast('Erreur lors du chargement des demandes', 'error');
        return;
      }

      // Récupérer les profils des étudiants séparément car etudiant_id → auth.users(id)
      if (allMatches && allMatches.length > 0) {
        const etudiantUserIds = allMatches.map(match => match.etudiant_id);
        const { data: etudiantProfiles, error: profilesError } = await supabase
          .from('profils')
          .select('user_id, id, prenom, nom, filiere')
          .in('user_id', etudiantUserIds);

        if (!profilesError && etudiantProfiles) {
          // Associer les profils aux matchs
          allMatches.forEach((match: any) => {
            const profile = etudiantProfiles.find(p => p.user_id === match.etudiant_id);
            if (profile) {
              match.etudiant_profile_data = profile;
            }
          });
        }
      }

      // Transformer les données pour correspondre au format attendu
      const transformedData = allMatches?.map((match: any) => ({
        id: match.id,
        student_id: match.etudiant_id,
        tutor_id: match.repetiteur_id,
        status: match.liked === false ? ('rejected' as const) : (match.like_tuteur ? ('accepted' as const) : ('pending' as const)),
        message: '',
        created_at: match.created_at,
        student_profile: match.etudiant_profile_data ? {
          id: match.etudiant_profile_data.id,
          user_id: match.etudiant_profile_data.user_id,
          prenom: match.etudiant_profile_data.prenom,
          nom: match.etudiant_profile_data.nom,
          filiere: match.etudiant_profile_data.filiere,
          matieres: '', // Champ requis mais pas disponible
          tarif: 0, // Champ requis mais pas pertinent pour un étudiant
          role: 'etudiant' as const, // Type requis
          annee: match.etudiant_profile_data.annee,
          created_at: match.created_at,
          user: match.etudiant_profile ? { email: match.etudiant_profile.email } : undefined
        } as Profile : undefined
      })) || [];

      setRequests(transformedData);
  };

  const loadSentRequests = async (profile: any) => {
    if (!profile) {
      setSentRequests([]);
      return;
    }

    // Récupérer les demandes envoyées par cet utilisateur (en tant qu'étudiant)
    const { data: sentMatches, error: sentError } = await supabase
      .from('matchs')
      .select('*')
      .eq('etudiant_id', user!.id)  // Demandes envoyées par cet utilisateur
      .eq('like_etudiant', true)
      .order('created_at', { ascending: false });

    if (sentError) {
      return;
    }

    // Récupérer les profils des tuteurs
    if (sentMatches && sentMatches.length > 0) {
      const tutorIds = sentMatches.map(match => match.repetiteur_id);
      const { data: tutorProfiles, error: tutorProfilesError } = await supabase
        .from('profils')
        .select('id, user_id, prenom, nom, filiere')
        .in('id', tutorIds);

      if (!tutorProfilesError && tutorProfiles) {
        sentMatches.forEach((match: any) => {
          const tutorProfile = tutorProfiles.find(p => p.id === match.repetiteur_id);
          if (tutorProfile) {
            match.tuteur_profile_data = tutorProfile;
          }
        });
      }
    }

    // Transformer les demandes envoyées
    const transformedSentData = sentMatches?.map((match: any) => ({
      id: match.id,
      student_id: match.etudiant_id,
      tutor_id: match.repetiteur_id,
      status: match.liked === false ? ('rejected' as const) : (match.like_tuteur ? ('accepted' as const) : ('pending' as const)),
      message: '',
      created_at: match.created_at,
      tutor_profile: match.tuteur_profile_data ? {
        id: match.tuteur_profile_data.id,
        user_id: match.tuteur_profile_data.user_id,
        prenom: match.tuteur_profile_data.prenom,
        nom: match.tuteur_profile_data.nom,
        filiere: match.tuteur_profile_data.filiere,
        matieres: '',
        tarif: 0,
        role: 'repetiteur' as const,
        created_at: match.created_at
      } as Profile : undefined
    })) || [];

    setSentRequests(transformedSentData);
  };

  useEffect(() => {
    if (user) {
      loadRequests();
    }
  }, [user, loadRequests]);

  const handleRequestAction = async (requestId: string, action: 'accepted' | 'rejected') => {
    try {
      setProcessingId(requestId);
      
      if (action === 'accepted') {
        // Pour accepter, mettre à jour like_tuteur à true
        const { error } = await supabase
          .from('matchs')
          .update({ like_tuteur: true })
          .eq('id', requestId);

        if (error) {
          showToast('Erreur lors de l\'acceptation', 'error');
          return;
        }
        
      } else {
        // Pour refuser, marquer liked = false pour garder l'historique
        const { error } = await supabase
          .from('matchs')
          .update({ liked: false })
          .eq('id', requestId);

        if (error) {
          showToast('Erreur lors du refus', 'error');
          return;
        }
        
      }

      // Recharger la liste et les notifications
      await loadRequests();
      refreshNotifications();

      if (action === 'accepted') {
        showToast('Demande acceptée avec succès ! Les informations de contact sont maintenant disponibles.', 'success');
      } else {
        showToast('Match décliné. Le tuteur n\'est peut-être plus disponible.', 'success');
      }
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleViewContact = (userId: string) => {
    setSelectedStudentUserId(userId);
    setShowContactInfoModal(true);
  };

  const openRatingForStudent = (studentProfileId?: string) => {
    if (!studentProfileId) return;
    setRatingTargetProfileId(studentProfileId);
    setRatingOpen(true);
  };

  const cleanupRejectedRequests = async () => {
    if (!user) return;
    
    try {
      // Récupérer le profil tuteur
      const { data: profile, error: profileError } = await api.profiles.getByUserId(user.id);
      if (profileError || !profile) return;

      // Supprimer définitivement les demandes refusées
      const { error } = await supabase
        .from('matchs')
        .delete()
        .eq('repetiteur_id', profile.id)
        .eq('liked', false);

      if (error) {
        showToast('Erreur lors du nettoyage des demandes refusées', 'error');
        return;
      }

      // Recharger la liste
      await loadRequests();
      showToast('Demandes refusées supprimées avec succès', 'success');
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'En attente';
      case 'accepted':
        return 'Acceptée';
      case 'rejected':
        return 'Refusée';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#002c46] mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des demandes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 bg-[#002c46] rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-envelope text-white text-2xl"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Demandes de contact
          </h1>
          <p className="text-gray-600 mb-6">
            Gérez vos demandes de contact
          </p>
          
          {/* Onglets pour basculer entre demandes reçues et envoyées */}
          <div className="flex justify-center mb-6">
            <div className="bg-white rounded-lg p-1 shadow-md">
              <button
                onClick={() => setActiveTab('received')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'received' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="fas fa-inbox mr-2"></i>
                Reçues ({requests.length})
              </button>
              <button
                onClick={() => setActiveTab('sent')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'sent' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="fas fa-paper-plane mr-2"></i>
                Envoyées ({sentRequests.length})
              </button>
            </div>
          </div>
          
          {/* Bouton de nettoyage (seulement si on a des demandes) */}
          {((activeTab === 'received' && requests.length > 0) || (activeTab === 'sent' && sentRequests.length > 0)) && (
            <button
              onClick={cleanupRejectedRequests}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium mb-4"
            >
              <i className="fas fa-broom mr-2"></i>
              Nettoyer les demandes refusées
            </button>
          )}
        </div>

        {(activeTab === 'received' ? requests : sentRequests).length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-gray-400 text-6xl mb-6">
              <i className="fas fa-inbox"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {activeTab === 'received' ? 'Aucune demande reçue' : 'Aucune demande envoyée'}
            </h3>
            <p className="text-gray-600">
              {activeTab === 'received' 
                ? 'Vous n\'avez pas encore reçu de demandes de contact d\'étudiants.'
                : 'Vous n\'avez pas encore envoyé de demandes à des tuteurs.'
              }
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {activeTab === 'received' 
                  ? `${requests.length} demande${requests.length > 1 ? 's' : ''} reçue${requests.length > 1 ? 's' : ''}`
                  : `${sentRequests.length} demande${sentRequests.length > 1 ? 's' : ''} envoyée${sentRequests.length > 1 ? 's' : ''}`
                }
              </h2>
            </div>
            
            <div className="divide-y divide-gray-200">
              {(activeTab === 'received' ? requests : sentRequests).map((request) => (
                <div key={request.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <div className="w-10 h-10 bg-[#002c46]/10 rounded-full flex items-center justify-center mr-3">
                          <i className="fas fa-user text-[#002c46]"></i>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {activeTab === 'received' 
                              ? `${request.student_profile?.prenom} ${request.student_profile?.nom}`
                              : `${request.tutor_profile?.prenom} ${request.tutor_profile?.nom}`
                            }
                          </h3>
                          <p className="text-sm text-gray-600">
                            {activeTab === 'received' 
                              ? `${request.student_profile?.filiere} - ${formatAnnee(request.student_profile?.annee)}`
                              : `${request.tutor_profile?.filiere} - Tuteur`
                            }
                          </p>
                        </div>
                      </div>
                      
                      {request.message && (
                        <div className="bg-gray-50 rounded-lg p-3 mb-3">
                          <p className="text-sm text-gray-700">"{request.message}"</p>
                        </div>
                      )}
                      
                      <div className="flex items-center text-sm text-gray-500">
                        <i className="fas fa-clock mr-1"></i>
                        {new Date(request.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(request.status)}`}>
                        {getStatusText(request.status)}
                      </span>
                      
                      {request.status === 'pending' && (
                        <div className="flex space-x-2">
                          {activeTab === 'received' ? (
                            // Actions pour les demandes reçues (tuteur)
                            <>
                              <button
                                onClick={() => handleRequestAction(request.id, 'rejected')}
                                disabled={processingId === request.id}
                                className="px-3 py-1 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                              >
                                {processingId === request.id ? (
                                  <i className="fas fa-spinner fa-spin"></i>
                                ) : (
                                  <>
                                    <i className="fas fa-times mr-1"></i>
                                    Refuser
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => handleRequestAction(request.id, 'accepted')}
                                disabled={processingId === request.id}
                                className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                              >
                                {processingId === request.id ? (
                                  <i className="fas fa-spinner fa-spin"></i>
                                ) : (
                                  <>
                                    <i className="fas fa-check mr-1"></i>
                                    Accepter
                                  </>
                                )}
                              </button>
                            </>
                          ) : (
                            // Statut pour les demandes envoyées (étudiant)
                            <div className="text-sm text-gray-500">
                              <i className="fas fa-clock mr-1"></i>
                              En attente de réponse du tuteur
                            </div>
                          )}
                        </div>
                      )}
                      
                      {request.status === 'accepted' && (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewContact(activeTab === 'received' 
                              ? request.student_id  // user_id de l'étudiant
                              : request.tutor_profile?.user_id || request.tutor_id  // user_id du tuteur
                            )}
                            className="px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <i className="fas fa-eye mr-1"></i>
                            Voir contact
                          </button>
                          {/* Notation rapide */}
                          <div className="hidden sm:flex items-center ml-2">
                            {[1,2,3,4,5].map(i => (
                              <i
                                key={i}
                                className="fas fa-star text-yellow-400 cursor-pointer text-sm mx-0.5"
                                onClick={() => openRatingForStudent(request.student_profile?.id)}
                                title="Noter cet étudiant"
                              ></i>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bouton de retour */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/profile')}
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Retour au profil
          </button>
        </div>
      </div>

      {/* Modal pour afficher les informations de contact */}
      {selectedStudentUserId && (
        <ContactInfoModal
          isOpen={showContactInfoModal}
          onClose={() => {
            setShowContactInfoModal(false);
            setSelectedStudentUserId(null);
          }}
          matchedUserId={selectedStudentUserId}
          isStudent={activeTab === 'received'} // true pour étudiant (demandes reçues), false pour tuteur (demandes envoyées)
        />
      )}

      {/* Modal de notation (côté tuteur → étudiant) */}
      {ratingTargetProfileId && (
        <RatingModal
          isOpen={ratingOpen}
          tutorId={ratingTargetProfileId}
          onClose={() => setRatingOpen(false)}
          onSuccess={() => {
            setRatingOpen(false);
            showToast('Merci pour votre évaluation !', 'success');
          }}
          showToast={showToast}
        />
      )}
    </div>
  );
};